import {
  type DataTypeConfig,
  DataTypeConfigU,
  DataTypeConfigUtils,
  type InputObjectDataTypeConfig,
  type ListDataTypeConfig,
  type ScalarDataTypeConfig,
  type ScalarDefinition,
} from '@helo/cms'

import type { RenderPipelineContext } from '../RenderPipeline'
import {
  CMS_OPTIONS_TYPE_NAME,
  type CreateNestedOptionInput,
  type CreateOptionInput,
  type OptionCommon,
  type OptionsResolvedTypeOf,
  type OptionsState,
  type OptionsTypeOf,
} from './Options.types'

export { CMS_OPTIONS_TYPE_NAME } from './Options.types'
export type { OptionsState } from './Options.types'

type OptionsCtx<I, O> = RenderPipelineContext<I, O>

type OverlaySource<I = object, O = unknown> =
  | object
  | ((ctx: OptionsCtx<I, O>) => object)

const isOptionsCtx = (value: object): value is OptionsCtx<object, unknown> =>
  'input' in value &&
  'output' in value &&
  typeof (value as { input: unknown }).input === 'object' &&
  (value as { input: unknown }).input !== null

const inputBag = (value: object): Record<string, unknown> =>
  (isOptionsCtx(value) ? value.input : value) as Record<string, unknown>

const withInputBag = <V extends object>(
  value: V,
  next: Record<string, unknown>,
): V => (isOptionsCtx(value) ? { ...value, input: next } : next) as V

const hasOwn = (value: object, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(value, key)

const optionPresence = <
  I extends { required?: boolean; defaultValue?: unknown },
>(
  input: I,
) => ({
  isNullable: input.required === true ? false : true,
  defaultValue: 'defaultValue' in input ? input.defaultValue : null,
})

const optionMeta = (category?: string) => ({
  path: [] as [],
  unmappedPath: [] as [],
  ...(category == null ? {} : { category }),
})

const ofTypeForInputKind = (inputKind: string) => {
  if (inputKind === 'boolean') {
    return DataTypeConfigU.createScalarDataTypeConfig({
      typeName: 'Boolean',
      isNullable: false,
    })
  }
  if (inputKind === 'number') {
    return DataTypeConfigU.createScalarDataTypeConfig({
      typeName: 'Float',
      isNullable: false,
    })
  }
  return DataTypeConfigU.createScalarDataTypeConfig({
    typeName: 'String',
    isNullable: false,
  })
}

const scalar = <T>(typeName: string) =>
  ({
    ...DataTypeConfigU.createScalarDataTypeConfig({
      typeName,
      isNullable: false,
    }),
    typeName,
    isNullable: false as const,
    __tsType: undefined as unknown as T,
  }) as ScalarDataTypeConfig<ScalarDefinition<string, T>>

const list = <T extends DataTypeConfig>(ofType: T) =>
  ({
    ...DataTypeConfigU.createListDataTypeConfig({
      isNullable: false,
      ofType,
    }),
    isNullable: false as const,
    ofType,
  }) as ListDataTypeConfig<T>

interface CreatedOption<
  I extends OptionCommon & { inputKind: string } = CreateOptionInput,
> {
  kind: 'INPUT_VALUE'
  typeName: 'InputValue'
  key: I['key']
  isNullable: I extends { required: true } ? false : true
  defaultValue: I extends { defaultValue: infer D } ? D : null
  ofType: I extends { ofType: infer O } ? O : DataTypeConfig
  directives: []
  state: {
    label: I['label']
    description: I['description']
    inputKind: I['inputKind']
    selectChoices: I extends { selectChoices: infer C } ? C : undefined
    min: I extends { min: infer M } ? M : undefined
    max: I extends { max: infer M } ? M : undefined
    step: I extends { step: infer S } ? S : undefined
  }
  meta: {
    path: []
    unmappedPath: []
    category: I['category']
  }
}

interface CreatedNested<I extends CreateNestedOptionInput> {
  kind: 'INPUT_VALUE'
  typeName: 'InputValue'
  key: I['key']
  isNullable: I extends { required: true } ? false : true
  defaultValue: I extends { defaultValue: infer D } ? D : null
  ofType: DataTypeConfig
  directives: []
  state: {
    label: I['label']
    description: I['description']
    inputKind: I extends { list: true } ? 'options_list' : 'options_object'
  }
  meta: {
    path: []
    unmappedPath: []
    category: I['category']
  }
}

type CreateOption = {
  <
    const I extends OptionCommon & {
      inputKind: 'boolean'
      required?: boolean
      defaultValue?: unknown
    },
  >(
    input: I &
      (
        | { required: true; defaultValue?: never }
        | { required?: false; defaultValue: boolean }
        | { required?: false; defaultValue?: undefined }
      ),
  ): CreatedOption<I>
  <
    const I extends OptionCommon & {
      inputKind: 'number'
      min?: number
      max?: number
      step?: number
      required?: boolean
      defaultValue?: unknown
    },
  >(
    input: I &
      (
        | { required: true; defaultValue?: never }
        | { required?: false; defaultValue: number }
        | { required?: false; defaultValue?: undefined }
      ),
  ): CreatedOption<I>
  <
    const I extends OptionCommon & {
      inputKind: 'text' | 'color'
      required?: boolean
      defaultValue?: unknown
    },
  >(
    input: I &
      (
        | { required: true; defaultValue?: never }
        | { required?: false; defaultValue: string }
        | { required?: false; defaultValue?: undefined }
      ),
  ): CreatedOption<I>
  <
    const I extends OptionCommon & {
      inputKind: 'select'
      selectChoices: readonly [
        { value: string; label: string },
        ...{ value: string; label: string }[],
      ]
      required?: boolean
      defaultValue?: unknown
    },
  >(
    input: I &
      (
        | { required: true; defaultValue?: never }
        | {
            required?: false
            defaultValue: I['selectChoices'][number]['value']
          }
        | { required?: false; defaultValue?: undefined }
      ),
  ): CreatedOption<I>
  <
    const I extends OptionCommon & {
      inputKind: string
      ofType: DataTypeConfig
      required?: boolean
      defaultValue?: unknown
    },
  >(
    input: I,
  ): CreatedOption<I>
}

const createOption = ((input: CreateOptionInput) => {
  const ofType =
    'ofType' in input && input.ofType != null
      ? input.ofType
      : ofTypeForInputKind(input.inputKind)

  return DataTypeConfigU.createInputValueDataTypeConfig({
    key: input.key,
    ...optionPresence(input),
    ofType,
    state: {
      label: input.label,
      description: input.description,
      inputKind: input.inputKind,
      ...('selectChoices' in input
        ? { selectChoices: input.selectChoices }
        : {}),
      ...('min' in input ? { min: input.min } : {}),
      ...('max' in input ? { max: input.max } : {}),
      ...('step' in input ? { step: input.step } : {}),
    },
    meta: optionMeta(input.category),
  })
}) as CreateOption

const nestedReference = () =>
  DataTypeConfigU.createTypeReferenceDataTypeConfig({
    typeName: CMS_OPTIONS_TYPE_NAME,
    isNullable: false,
  })

const createNested = <const I extends CreateNestedOptionInput>(
  input: I,
): CreatedNested<I> =>
  DataTypeConfigU.createInputValueDataTypeConfig({
    key: input.key,
    ...optionPresence(input),
    ofType:
      input.list === true
        ? DataTypeConfigU.createListDataTypeConfig({
            isNullable: false,
            ofType: nestedReference(),
          })
        : nestedReference(),
    state: {
      label: input.label,
      description: input.description,
      inputKind: input.list === true ? 'options_list' : 'options_object',
    },
    meta: optionMeta(input.category),
  }) as unknown as CreatedNested<I>

type OptionField = {
  key: string
  defaultValue?: unknown
  isNullable?: boolean
}

type OptionFields = Record<string, OptionField> | InputObjectDataTypeConfig

const isInputObject = (
  options: OptionFields,
): options is InputObjectDataTypeConfig =>
  'kind' in options && options.kind === 'INPUT_OBJECT'

const fieldsOf = (options: OptionFields): OptionField[] =>
  isInputObject(options) ? options.fields : Object.values(options)

const asRecord = <const T extends readonly { key: string }[]>(
  ...options: T
): { [O in T[number] as O['key']]: O } =>
  Object.fromEntries(options.map(option => [option.key, option])) as {
    [O in T[number] as O['key']]: O
  }

const toInputObject = <
  const TName extends string,
  const T extends Record<string, OptionField>,
>(
  typeName: TName,
  record: T,
) =>
  DataTypeConfigU.createInputObjectDataTypeConfig({
    typeName,
    fields: Object.values(record).map(field => ({
      ...field,
      isNullable: true,
    })) as never,
  }) as unknown as InputObjectDataTypeConfig<TName, T[keyof T]> & {
    typeName: TName
    fields: T[keyof T][]
  }

let cmsOptionRegistry: DataTypeConfig[] | undefined

const bindCmsOptionRegistry = (registry: DataTypeConfig[]) => {
  cmsOptionRegistry = registry
}

const resolveSource = <I extends object, O>(
  source: OverlaySource<I, O>,
  ctx: OptionsCtx<I, O>,
): object => (typeof source === 'function' ? source(ctx) : source)

const fallback =
  <const T extends OptionFields>(options: T, source: OverlaySource) =>
  <C extends object>(value: C): C => {
    const fields = fieldsOf(options)
    const input = inputBag(value)
    if (fields.every(field => input[field.key] !== undefined)) {
      return value
    }
    const next = { ...input }
    const from = resolveSource(
      source,
      isOptionsCtx(value) ? value : { input: value, output: undefined },
    ) as Record<string, unknown> | null
    if (from == null) return value
    for (const field of fields) {
      if (
        next[field.key] === undefined &&
        hasOwn(from, field.key) &&
        from[field.key] !== undefined
      ) {
        next[field.key] = from[field.key]
      }
    }
    return withInputBag(value, next)
  }

const applyDefaults =
  <const T extends OptionFields>(options: T) =>
  <I extends object>(value: I): I => {
    const next = { ...inputBag(value) }
    for (const field of fieldsOf(options)) {
      if (field.defaultValue != null && next[field.key] === undefined) {
        next[field.key] = field.defaultValue
      }
    }
    return withInputBag(value, next)
  }

const isNestedReference = (field: OptionField): boolean => {
  const ofType = (
    field as { ofType?: { kind?: string; ofType?: { kind?: string } } }
  ).ofType
  if (ofType?.kind === 'TYPE_REFERENCE') return true
  return ofType?.kind === 'LIST' && ofType.ofType?.kind === 'TYPE_REFERENCE'
}

const validate =
  <const T extends OptionFields>(options: T) =>
  <C extends object>(value: C): C => {
    const config = isInputObject(options)
      ? options
      : DataTypeConfigU.createInputObjectDataTypeConfig({
          typeName: 'Options',
          fields: fieldsOf(options).filter(
            field => !isNestedReference(field),
          ) as never,
        })
    DataTypeConfigUtils.validateData(
      inputBag(value),
      cmsOptionRegistry,
    )(config as DataTypeConfig)
    return value
  }

export const Options = {
  scalar,
  list,
  createOption,
  createNested,
  asRecord,
  toInputObject,
  fallback,
  applyDefaults,
  validate,
  bindCmsOptionRegistry,
}

export namespace Options {
  export type TypeOf<T> = OptionsTypeOf<T>
  export type ResolvedTypeOf<T> = OptionsResolvedTypeOf<T>
  export type State = OptionsState
}

export type { OptionsResolvedTypeOf, OptionsTypeOf }
